import React, { createContext, Dispatch, SetStateAction, useCallback } from 'react'
import { omitBy, isUndefined } from '@golden/utils'
import { FinanceDepositWayGroupForm, FinanceDepositWayGroupSearchQuery } from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import EditFinanceDepositWayGroupForm, { EditFinanceDepositWayGroupFormType, initialForm } from '../../../components/admin/finance/EditFinanceDepositWayGroupForm'
import useT from '../../../i18ns/admin/useT'
import { useDialogHandleSubmit, useRedirectHandleBack, useRequestFromSearch } from '../../../utils/default/ComplexFlowHook'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import allRoute from '../../../components/admin/route/route'
import { searchToRequest } from './FinanceDepositWayPage'
import StateProvider from '../../../providers/default/StateProvider'

const formToRequest: (form: EditFinanceDepositWayGroupFormType) => FinanceDepositWayGroupForm = (form) => {
  const converted = {
    name: form.name,
    is_active: form.isActive,
    method: form.method === '--' ? undefined : form.method,
    support_accounts: form.supportAccounts,
    support_ways: form.supportWays
  } as FinanceDepositWayGroupForm
  return omitBy(converted, isUndefined) as FinanceDepositWayGroupForm
}

const defaultForm = initialForm()

export const SearchStateContext = createContext<[FinanceDepositWayGroupSearchQuery, Dispatch<SetStateAction<FinanceDepositWayGroupSearchQuery>>]>([{}, () => {}])

const FinanceDepositWayGroupCreatePage: React.FC = () => {
  const request = useRequestFromSearch({ searchToRequest })
  const classes = useCommonStyles()
  const globalDialog = useGlobalDialog()
  const gdk = useGDK()
  const { t } = useT()

  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({
    path: allRoute.financeDepositWayGroup.encodePath({ search: { ...request }, param: {} })
  })

  const { handleSubmit } = useDialogHandleSubmit<EditFinanceDepositWayGroupFormType, FinanceDepositWayGroupForm, null>({
    dialogId: 'createWayGroup',
    globalDialog,
    getChangeDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      message: (
        <React.Fragment>
          {t('dialog.confirmCreate')}
        </React.Fragment>
      )
    }), [t, classes.purpleWord]),
    getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.createDepositSuccess')
    }), [t]),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    formToRequest,
    gdkFunc: (payload) => gdk.finance.createDepositWayGroup(payload),
    gdkFuncDependencies: [gdk],
    afterSuccessDialog: handleBack
  })

  return (
    <StateProvider
      context={SearchStateContext}
      defaultValue={{}}
    >
      <Box padding={5}>
        <EditFinanceDepositWayGroupForm
          title={t('common.createDepositWayGroup')}
          okText={t('common.confirmCreate')}
          onBack={handleDebouncedBack}
          onSubmit={handleSubmit}
          defaultValue={defaultForm}
          searchContext={SearchStateContext}
        />
      </Box>
    </StateProvider>
  )
}

export default React.memo(FinanceDepositWayGroupCreatePage)
